import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateProfile } from "../../store/slices/authSlice";
import { fetchSubscriptionStatus, fetchTiers, createCheckout, openBillingPortal } from "../../store/slices/subscriptionSlice";
import { CheckCircleIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";

const PRESET_COLORS = [
  "#4F46E5", "#7C3AED", "#DB2777", "#DC2626", "#EA580C",
  "#D97706", "#059669", "#0284C7", "#1D4ED8", "#374151",
];

export default function Settings() {
  const dispatch = useDispatch();
  const { agent } = useSelector((state) => state.auth);
  const { status: subStatus, tiers, isInTrial, quotesUsed, quotesLimit } = useSelector((state) => state.subscription);
  const [saved, setSaved] = useState(false);
  const [billingCycle, setBillingCycle] = useState("monthly");

  useEffect(() => {
    dispatch(fetchSubscriptionStatus());
    dispatch(fetchTiers());
  }, [dispatch]);

  const [form, setForm] = useState({
    name: agent?.name || "",
    company: agent?.company || "",
    phone: agent?.phone || "",
    brandColor: agent?.brandColor || "#4F46E5",
    defaultExpiryDays: agent?.defaultExpiryDays || 14,
    emailSignature: agent?.emailSignature || "",
    logo: agent?.logo || "",
    agentPhoto: agent?.agentPhoto || "",
    website: agent?.website || "",
    tagline: agent?.tagline || "",
  });

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    await dispatch(updateProfile(form));
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  return (
    <div className="max-w-3xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Settings</h1>

      {saved && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-2">
          <CheckCircleIcon className="h-5 w-5 text-green-500" />
          <span className="text-sm text-green-700 font-medium">Settings saved successfully</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Profile */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Profile</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
              <input
                name="name"
                value={form.name}
                onChange={handleChange}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Company / Agency Name</label>
              <input
                name="company"
                value={form.company}
                onChange={handleChange}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <input
                name="phone"
                value={form.phone}
                onChange={handleChange}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
              <input
                name="website"
                value={form.website}
                onChange={handleChange}
                placeholder="https://youragency.com"
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Tagline</label>
              <input
                name="tagline"
                value={form.tagline}
                onChange={handleChange}
                placeholder="Your trusted cruise travel experts"
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>
        </div>

        {/* Branding */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-1">Branding</h2>
          <p className="text-sm text-gray-500 mb-4">
            Customize how your quotes look to clients. Your brand color, logo, and photo appear on all client-facing quotes.
          </p>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Brand Color</label>
              <div className="flex items-center gap-3 flex-wrap">
                {PRESET_COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setForm({ ...form, brandColor: color })}
                    className={`h-10 w-10 rounded-full border-2 transition-all ${
                      form.brandColor === color ? "border-gray-900 scale-110 ring-2 ring-offset-2 ring-gray-400" : "border-transparent"
                    }`}
                    style={{ backgroundColor: color }}
                  />
                ))}
                <div className="flex items-center gap-2 ml-2">
                  <input
                    type="color"
                    value={form.brandColor}
                    onChange={(e) => setForm({ ...form, brandColor: e.target.value })}
                    className="h-10 w-10 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={form.brandColor}
                    onChange={(e) => setForm({ ...form, brandColor: e.target.value })}
                    className="w-24 px-2 py-1.5 border border-gray-300 rounded text-sm"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Logo URL</label>
              <input
                name="logo"
                value={form.logo}
                onChange={handleChange}
                placeholder="https://youragency.com/logo.png"
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
              />
              {form.logo && (
                <div className="mt-2 p-3 bg-gray-50 rounded-lg inline-block">
                  <img src={form.logo} alt="Logo preview" className="max-h-16 object-contain" onError={(e) => e.target.style.display = 'none'} />
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Agent Photo URL</label>
              <input
                name="agentPhoto"
                value={form.agentPhoto}
                onChange={handleChange}
                placeholder="https://youragency.com/photo.jpg"
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
              />
              {form.agentPhoto && (
                <div className="mt-2 inline-block">
                  <img src={form.agentPhoto} alt="Agent photo" className="h-16 w-16 rounded-full object-cover" onError={(e) => e.target.style.display = 'none'} />
                </div>
              )}
            </div>

            {/* Preview */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Preview</label>
              <div className="border border-gray-200 rounded-xl overflow-hidden max-w-sm">
                <div className="p-6 text-center text-white" style={{ backgroundColor: form.brandColor }}>
                  {form.logo ? (
                    <img src={form.logo} alt="Logo" className="max-h-10 mx-auto mb-2 object-contain" onError={(e) => e.target.style.display = 'none'} />
                  ) : (
                    <h3 className="text-lg font-bold">{form.company || form.name || "Your Agency"}</h3>
                  )}
                  {form.tagline && <p className="text-sm opacity-90">{form.tagline}</p>}
                </div>
                <div className="p-4 bg-gray-50">
                  <div className="flex items-center gap-3">
                    {form.agentPhoto ? (
                      <img src={form.agentPhoto} alt="" className="h-10 w-10 rounded-full object-cover" onError={(e) => e.target.style.display = 'none'} />
                    ) : (
                      <div className="h-10 w-10 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{ backgroundColor: form.brandColor }}>
                        {form.name?.charAt(0) || "A"}
                      </div>
                    )}
                    <div>
                      <p className="text-sm font-medium text-gray-900">{form.name || "Agent Name"}</p>
                      <p className="text-xs text-gray-500">{form.company || "Your Agency"}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Email Signature */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-1">Email Signature</h2>
          <p className="text-sm text-gray-500 mb-4">
            This signature appears at the bottom of quote emails sent to clients.
          </p>
          <textarea
            name="emailSignature"
            value={form.emailSignature}
            onChange={handleChange}
            rows={4}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
            placeholder="Best regards,&#10;Jane Smith&#10;Luxury Travel Advisors&#10;1-800-555-0123"
          />
        </div>

        {/* Subscription */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-1">Subscription</h2>
          <p className="text-sm text-gray-500 mb-4">Manage your QuickQuote plan</p>

          {/* Current status */}
          {subStatus && (
            <div className="mb-4">
              {isInTrial && (
                <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-lg mb-4">
                  <div className="flex items-center gap-2">
                    <ExclamationTriangleIcon className="h-5 w-5 text-indigo-600" />
                    <span className="font-medium text-indigo-800">Free Trial Active</span>
                  </div>
                  <p className="text-sm text-indigo-600 mt-1">
                    Your Professional trial ends on {new Date(subStatus.trialEndsAt).toLocaleDateString()}.
                    Subscribe to keep your account active.
                  </p>
                </div>
              )}

              {subStatus.subscription && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg mb-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-green-800">
                        {subStatus.tierDetails?.name} Plan - Active
                      </p>
                      <p className="text-sm text-green-600">
                        {quotesUsed} / {quotesLimit === -1 ? "Unlimited" : quotesLimit} quotes used this month
                      </p>
                    </div>
                    <button
                      onClick={() => dispatch(openBillingPortal())}
                      className="text-sm text-green-700 font-medium hover:underline"
                    >
                      Manage Billing
                    </button>
                  </div>
                </div>
              )}

              {!isInTrial && !subStatus.subscription && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg mb-4">
                  <p className="font-medium text-red-800">No Active Subscription</p>
                  <p className="text-sm text-red-600">Subscribe to start sending quotes</p>
                </div>
              )}
            </div>
          )}

          {/* Pricing tiers */}
          {(!subStatus?.subscription || isInTrial) && tiers.length > 0 && (
            <div>
              <div className="flex justify-center mb-4">
                <div className="inline-flex bg-gray-100 rounded-lg p-1">
                  <button
                    type="button"
                    onClick={() => setBillingCycle("monthly")}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md ${billingCycle === "monthly" ? "bg-white shadow-sm" : ""}`}
                  >
                    Monthly
                  </button>
                  <button
                    type="button"
                    onClick={() => setBillingCycle("yearly")}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md ${billingCycle === "yearly" ? "bg-white shadow-sm" : ""}`}
                  >
                    Yearly (save 17%)
                  </button>
                </div>
              </div>
              <div className="grid md:grid-cols-3 gap-3">
                {tiers.map((tier) => (
                  <div key={tier.id} className={`border rounded-lg p-4 ${tier.popular ? "border-indigo-500 bg-indigo-50/50" : "border-gray-200"}`}>
                    {tier.popular && <p className="text-xs font-bold text-indigo-600 mb-1">MOST POPULAR</p>}
                    <h3 className="font-semibold text-gray-900">{tier.name}</h3>
                    <p className="text-2xl font-bold text-gray-900 mt-1">
                      ${billingCycle === "yearly" ? Math.round(tier.yearlyPrice / 12) : tier.monthlyPrice}
                      <span className="text-sm text-gray-500 font-normal">/mo</span>
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {tier.quotesPerMonth === -1 ? "Unlimited" : tier.quotesPerMonth} quotes/month
                    </p>
                    <button
                      type="button"
                      onClick={() => dispatch(createCheckout({ tier: tier.id, billing: billingCycle }))}
                      className={`w-full mt-3 py-2 rounded-lg text-sm font-medium ${
                        tier.popular
                          ? "bg-indigo-600 text-white hover:bg-indigo-700"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                    >
                      Subscribe
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Quote Defaults */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Quote Defaults</h2>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Default Quote Expiry</label>
            <select
              name="defaultExpiryDays"
              value={form.defaultExpiryDays}
              onChange={handleChange}
              className="px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
            >
              <option value={7}>7 days</option>
              <option value={14}>14 days</option>
              <option value={21}>21 days</option>
              <option value={30}>30 days</option>
              <option value={60}>60 days</option>
            </select>
          </div>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            className="px-6 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Save Settings
          </button>
        </div>
      </form>
    </div>
  );
}
